/**
 * Provider minimum lead times for deferred first charges ($0 today).
 * OpenPix ONLY_RECURRENCY and Stripe trial_end both reject “too soon” dates;
 * clamp here so UI, orders, and provider payloads stay aligned.
 */
import type { PaymentMethod } from "./types.js";

export const OPENPIX_DEFERRED_MIN_LEAD_DAYS = 3;
export const OPENPIX_DAY_GENERATE_CHARGE_MAX = 27;
export const OPENPIX_DAY_DUE = 3;

/** Stripe: trial_end must be at least 2 days from now. */
export const STRIPE_DEFERRED_MIN_LEAD_MS = 2 * 24 * 60 * 60 * 1000;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** How far `resolveOpenPixOnlyRecurrencyDay` moves a date back to one Woovi starts on (at most 9 days seen). */
const OPENPIX_WALK_BACK_MAX_DAYS = 10;

const SAO_PAULO_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Sao_Paulo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function saoPauloParts(d: Date) {
  const parts = Object.fromEntries(SAO_PAULO_PARTS.formatToParts(d).map((p) => [p.type, p.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** The São Paulo calendar date of `d`, as midnight UTC of that date (getUTCDate() is the BRT day). */
export function saoPauloDay(d: Date): Date {
  const p = saoPauloParts(d);
  return new Date(Date.UTC(p.year, p.month - 1, p.day));
}

/** The instant the São Paulo day that contains `d` starts. */
export function startOfSaoPauloDay(d: Date): Date {
  const p = saoPauloParts(d);
  const wholeSeconds = Math.floor(d.getTime() / 1000) * 1000;
  const offsetMs = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - wholeSeconds;
  return new Date(Date.UTC(p.year, p.month - 1, p.day) - offsetMs);
}

export function utcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** The first `day` of a month on or after `from` (UTC date). */
export function firstOccurrenceOfDay(day: number, from: Date): Date {
  const sameMonth = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), day));
  if (sameMonth.getTime() >= from.getTime()) return sameMonth;
  return new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, day));
}

/**
 * Whether an ONLY_RECURRENCY agreement sent with `day`'s day of the month starts on that date.
 *
 * Woovi places a numeric `dayGenerateCharge` from today: below today's day it is next month, above
 * it this month (equal is not documented), and a start fewer than 3 days away is refused, never
 * moved a month on (developers.woovi.com, "pix-automatic-how-to-create"; the 400 "dayGenerateCharge
 * deve ser pelo menos três dias a partir de hoje" for day 1 on 30/10/2026, read as 01/11). So only
 * dates from today + 3 up to the day before today's day next month can be reached. Which "today"
 * Woovi counts from (the UTC or the São Paulo date) is not confirmed, so the date must land under
 * both; a day equal to today's is counted as today, which refuses it under either reading.
 */
export function openPixStartsOn(day: Date, now: Date = new Date()): boolean {
  const date = utcDay(day);
  if (date.getUTCDate() > OPENPIX_DAY_GENERATE_CHARGE_MAX) return false;
  return [utcDay(now), saoPauloDay(now)].every(
    (today) =>
      date.getTime() >= today.getTime() + OPENPIX_DEFERRED_MIN_LEAD_DAYS * MS_PER_DAY &&
      firstOccurrenceOfDay(date.getUTCDate(), today).getTime() === date.getTime(),
  );
}

/**
 * `firstChargeAt` moved to today + 3 (UTC) when sooner, and past day 27 to the 1st of the next
 * month (the accepted unpaid days). Whether Woovi starts on the result is `openPixStartsOn`'s call.
 */
export function rollOpenPixDay(firstChargeAt: Date, now: Date = new Date()): Date {
  let cursor = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + OPENPIX_DEFERRED_MIN_LEAD_DAYS),
  );
  const target = utcDay(firstChargeAt);
  if (target.getTime() > cursor.getTime()) cursor = target;

  while (cursor.getUTCDate() > OPENPIX_DAY_GENERATE_CHARGE_MAX) {
    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
  }
  return cursor;
}

/**
 * Woovi ONLY_RECURRENCY: the day of the month to send and the date Woovi then starts on: the rolled
 * date (`rollOpenPixDay`).
 *
 * With `moveBack` (a day-credit upgrade, where the date is when the credit runs out), a rolled date
 * Woovi would not start on (a month or more out, where Woovi takes this month's occurrence, or a 1st
 * under 3 days away) goes back to the last date before it that Woovi starts on, so the first charge
 * comes a few days before the credit ends instead of a 400 or a month early. Without it the rolled
 * date is sent as before: a change at the end of a paid period must not charge paid days, and the
 * provider call sends the date the order already holds. Those changes can still get the 400 or the
 * early charge on a period end Woovi cannot reach (open: BILLING_RULES.md, "Annual → monthly").
 */
export function resolveOpenPixOnlyRecurrencyDay(
  firstChargeAt: Date,
  now: Date = new Date(),
  opts: { moveBack?: boolean } = {},
): { dayGenerateCharge: number; effectiveFirstChargeAt: Date } {
  const rolled = rollOpenPixDay(firstChargeAt, now);
  for (let back = 0; opts.moveBack && back <= OPENPIX_WALK_BACK_MAX_DAYS; back++) {
    const day = new Date(rolled.getTime() - back * MS_PER_DAY);
    if (openPixStartsOn(day, now)) return { dayGenerateCharge: day.getUTCDate(), effectiveFirstChargeAt: day };
  }
  return { dayGenerateCharge: rolled.getUTCDate(), effectiveFirstChargeAt: rolled };
}

/**
 * Earliest allowed first-charge instant for a deferred provider setup. `dayCredit`: the date is when
 * a day-credit upgrade's credit runs out, so on PIX it may move a few days earlier to one Woovi starts
 * on (`resolveOpenPixOnlyRecurrencyDay`'s `moveBack`).
 */
export function clampDeferredFirstChargeAt(args: {
  paymentMethod: PaymentMethod;
  firstChargeAt: Date;
  now?: Date;
  dayCredit?: boolean;
}): Date {
  const now = args.now ?? new Date();
  if (args.paymentMethod === "openpix") {
    return resolveOpenPixOnlyRecurrencyDay(args.firstChargeAt, now, { moveBack: args.dayCredit })
      .effectiveFirstChargeAt;
  }
  if (args.paymentMethod === "stripe") {
    const min = new Date(now.getTime() + STRIPE_DEFERRED_MIN_LEAD_MS);
    return args.firstChargeAt.getTime() >= min.getTime() ? new Date(args.firstChargeAt) : min;
  }
  return new Date(args.firstChargeAt);
}

/** Days from `now` until `firstChargeAt` (fractional, ≥ 0). */
export function coveredDaysUntil(firstChargeAt: Date, now: Date = new Date()): number {
  return Math.max(0, (firstChargeAt.getTime() - now.getTime()) / MS_PER_DAY);
}
